import { renderToStaticMarkup } from 'react-dom/server';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as L from 'leaflet';
import { cn } from '@noor/lib';
import { resolveToneColor } from '../lib/offer-colors';
import { outcomeLabel, shortCourierId } from '../lib/offer-labels';
import { createOrderMap, toLatLng } from '../lib/osm-basemap';
import { OfferBadgeFace, OriginPinFace } from './offer-faces';
import { OffersMapControls } from './offers-map-controls';
import type { OffersCanvasProps } from './offers-canvas-yandex';

const BADGE_SIZE = 40;
const ORIGIN_SIZE = 36;
const FIT_PADDING: L.PointTuple = [40, 40];
const FIT_MAX_ZOOM = 16;

const toLatLngBounds = ([
  [minLng, minLat],
  [maxLng, maxLat],
]: OffersCanvasProps['bounds']) => L.latLngBounds([minLat, minLng], [maxLat, maxLng]);

/**
 * Offers visualization on the OSM/Leaflet basemap — the fallback when the
 * Yandex SDK has no key, so the modal is never blank. Same picture as
 * {@link OffersCanvasYandex}: pickup pin, courier badges, courier→pickup lines.
 */
export function OffersCanvasOsm({
  origin,
  markers,
  bounds,
  fitKey,
  selectedKey,
  onSelect,
  className,
}: OffersCanvasProps) {
  const { t } = useTranslation('orders');
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  // Latest props for handlers/effects that must not re-run on every change.
  const latest = useRef({ bounds, onSelect });
  // Declared before the effects below, so they always read the current props.
  useEffect(() => {
    latest.current = { bounds, onSelect };
  });

  useEffect(() => {
    if (!container.current) return;
    const { map: instance, dispose } = createOrderMap(container.current);
    map.current = instance;
    instance.on('click', () => latest.current.onSelect(null));
    return () => {
      dispose();
      map.current = null;
    };
  }, []);

  // Frame once per key (mount / a different order) — never on a data refresh.
  useEffect(() => {
    map.current?.fitBounds(toLatLngBounds(latest.current.bounds), {
      padding: FIT_PADDING,
      maxZoom: FIT_MAX_ZOOM,
    });
  }, [fitKey]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const layers = L.layerGroup().addTo(instance);

    markers.forEach((marker) => {
      const selected = marker.key === selectedKey;
      const emphasised = selected || marker.active;
      L.polyline([toLatLng(marker.coords), toLatLng(origin.coords)], {
        color: resolveToneColor(marker.outcome.tone),
        weight: emphasised ? 4 : 2,
        opacity: emphasised ? 1 : marker.opacity,
        interactive: false,
      }).addTo(layers);
    });

    L.marker(toLatLng(origin.coords), {
      icon: L.divIcon({
        html: renderToStaticMarkup(<OriginPinFace label={origin.label} />),
        className: '',
        iconSize: [ORIGIN_SIZE, ORIGIN_SIZE],
        iconAnchor: [ORIGIN_SIZE / 2, ORIGIN_SIZE / 2],
      }),
      zIndexOffset: 500,
      interactive: false,
      keyboard: false,
    }).addTo(layers);

    markers.forEach((marker) => {
      const selected = marker.key === selectedKey;
      const pin = L.marker(toLatLng(marker.coords), {
        icon: L.divIcon({
          html: renderToStaticMarkup(
            <OfferBadgeFace
              tone={marker.outcome.tone}
              km={marker.badgeKm}
              opacity={selected || marker.active ? 1 : marker.opacity}
              selected={selected}
              active={marker.active}
            />,
          ),
          className: '',
          iconSize: [BADGE_SIZE, BADGE_SIZE],
          iconAnchor: [BADGE_SIZE / 2, BADGE_SIZE / 2],
        }),
        zIndexOffset: selected ? 3000 : marker.active ? 2000 : 1000,
        title: t('offers.markerLabel', {
          id: shortCourierId(marker.offer.courierId) ?? '—',
          km: marker.badgeKm.toFixed(1),
          outcome: outcomeLabel(t, marker.outcome),
        }),
        alt: '',
      }).addTo(layers);
      pin.on('click', () => latest.current.onSelect(selected ? null : marker.key));
    });

    return () => {
      layers.remove();
    };
  }, [markers, origin.coords, origin.label, selectedKey, t]);

  const fit = useCallback(() => {
    map.current?.fitBounds(toLatLngBounds(latest.current.bounds), {
      padding: FIT_PADDING,
      maxZoom: FIT_MAX_ZOOM,
    });
  }, []);

  return (
    <div className={cn('relative isolate', className)} data-testid="order-offers-osm">
      <div ref={container} className="absolute inset-0" />
      <OffersMapControls
        onFit={fit}
        onZoomIn={() => map.current?.zoomIn()}
        onZoomOut={() => map.current?.zoomOut()}
      />
    </div>
  );
}
